import { useState, useEffect, createContext, useMemo, useCallback, ReactNode, startTransition, useRef } from 'react';
import { AxiosInstance } from 'axios';

import { ManagementService } from '../../../services/ManagementService';
import { AccountService } from '../../../services/AccountService';
import { ICompany, INotification } from '../../../services/Interfaces/IAuthService';
import { useApi } from '../../../services/hooks/apiHook';
import { IDocument, GetCompanyDocumentsResponse } from '../../../services/Interfaces/IAccountService';

import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';

import { getDocsFromShareholders } from '../../../utils/getDocsFromShareholders';
import { AuthService } from '../../../services/AuthService';
import { GetCompanyInfoResponse } from '../../../services/Interfaces/IManagemenntService';
import { useAuth } from '../../../store/auth';
import { BillingService } from '../../../services/BillingService';

type RefreshFunction<T = void> = (force?: boolean) => Promise<T | void>;

interface DashboardCustomerContextValue {
  companyInfo?: any;
  documents?: IDocument[];
  notifications: INotification[];
  companies: ICompany[];
  latestAnnualFee: any;
  unreadNotifications: number;
  refreshCompanyInfo: RefreshFunction;
  refreshDocuments: RefreshFunction<IDocument[]>;
  refreshNotifications: RefreshFunction<INotification[]>;
  refreshCompanies: RefreshFunction<ICompany[]>;
  refreshLatestAnnualFee: RefreshFunction<any>;
  refreshUserContract: (force?: boolean) => Promise<{ signed: boolean; url: string } | null | undefined>;
  markNotificationAsRead: (notificationId: number | string) => void;
  markAllNotificationsAsRead: () => void;
  isSigned: boolean;
  showMessage: boolean;
  urlContract: string;
  company?: ICompany;
  token?: string | null;
  fetchingData: boolean;
  fetchingCompanies: boolean;
  notificationsRefreshing: boolean;
  setNotificationsRefreshing: (value: boolean) => void;
}

const createAsyncNoop = <T = void>(): RefreshFunction<T> => () => Promise.resolve(undefined);

const markNotificationNoop: DashboardCustomerContextValue['markNotificationAsRead'] = (_notificationId: number | string) => undefined;
const markAllNotificationsNoop: DashboardCustomerContextValue['markAllNotificationsAsRead'] = () => undefined;

const defaultContextValue: DashboardCustomerContextValue = {
  companyInfo: undefined,
  documents: undefined,
  notifications: [],
  companies: [],
  latestAnnualFee: null,
  unreadNotifications: 0,
  refreshCompanyInfo: createAsyncNoop(),
  refreshDocuments: createAsyncNoop<IDocument[]>(),
  refreshNotifications: createAsyncNoop<INotification[]>(),
  refreshCompanies: createAsyncNoop<ICompany[]>(),
  refreshLatestAnnualFee: createAsyncNoop<any>(),
  refreshUserContract: () => Promise.resolve(null),
  markNotificationAsRead: markNotificationNoop,
  markAllNotificationsAsRead: markAllNotificationsNoop,
  isSigned: false,
  showMessage: false,
  urlContract: '',
  company: undefined,
  token: undefined,
  fetchingData: false,
  fetchingCompanies: false,
  notificationsRefreshing: false,
  setNotificationsRefreshing: () => undefined
};

const companyInfoCache = new Map<string, any>();
const companyInfoPromiseMap = new Map<string, Promise<any>>();
const companiesCache = new Map<string, ICompany[]>();
const companiesPromiseMap = new Map<string, Promise<ICompany[]>>();
const documentsCache = new Map<string, IDocument[]>();
const documentsPromiseMap = new Map<string, Promise<IDocument[]>>();
const notificationsCache = new Map<string, INotification[]>();
const notificationsPromiseMap = new Map<string, Promise<INotification[]>>();
const userContractCache = new Map<string, { signed: boolean; url: string } | null>();
const userContractPromiseMap = new Map<string, Promise<{ signed: boolean; url: string } | null>>();

const DashboardCustomerContext = createContext<DashboardCustomerContextValue>(defaultContextValue);

interface IDashBoardCustomerProviderProps {
  children: ReactNode;
}

const DashboardCustomerProvider = ({ children }: IDashBoardCustomerProviderProps) => {
  const [companyInfo, setCompanyInfo] = useState<any>();
  const [documents, setDocuments] = useState<IDocument[]>();
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [latestAnnualFee, setLatestAnnualFee] = useState<any>(null);

  const [isSigned, setIsSigned] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [urlContract, setUrlContact] = useState('');
  const [fetchingData, setFetchingData] = useState(false);
  const [fetchingCompanies, setFetchingCompanies] = useState(false);
  const [notificationsRefreshing, setNotificationsRefreshing] = useState(false);

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const api: AxiosInstance = useApi();
  const { company, selectCompany, token } = useAuth();

  const headers = useMemo(
    () => ({
      'Content-Language': legalPersonalitySlug,
      Authorization: token ? `Bearer ${token}` : ''
    }),
    [legalPersonalitySlug, token]
  );

  const buildCompaniesCacheKey = useCallback(
    () => `${token ?? ''}|${legalPersonalitySlug ?? ''}`,
    [token, legalPersonalitySlug]
  );

  const fetchCompanies = useCallback(
    async (force = false) => {
      if (!token) {
        setCompanies([]);
        return [] as ICompany[];
      }

      const cacheKey = buildCompaniesCacheKey();

      if (!force) {
        const cached = companiesCache.get(cacheKey);
        if (cached && cached.length) {
          setCompanies(cached);
          return cached;
        }

        const inFlight = companiesPromiseMap.get(cacheKey);
        if (inFlight) {
          return inFlight;
        }
      } else {
        companiesCache.delete(cacheKey);
        companiesPromiseMap.delete(cacheKey);
      }

      setFetchingCompanies(true);

      const request = AuthService.allCompanies(api, { headers })
        .then(({ data }) => {
          const companiesList = Array.isArray(data) ? data : [];
          companiesCache.set(cacheKey, companiesList);
          setCompanies(companiesList);

          if ((!company || company?.id === undefined) && companiesList.length) {
            selectCompany(companiesList[0]);
          }

          return companiesList;
        })
        .catch((error) => {
          console.error('Error fetching companies:', error);
          companiesCache.set(cacheKey, []);
          setCompanies([]);
          return [] as ICompany[];
        })
        .finally(() => {
          companiesPromiseMap.delete(cacheKey);
          setFetchingCompanies(false);
        });

      companiesPromiseMap.set(cacheKey, request);
      return request;
    },
    [api, buildCompaniesCacheKey, company, headers, selectCompany, token]
  );

  const buildCompanyInfoKey = useCallback(
    (companyId?: number | string) => {
      if (!companyId) {
        return '';
      }
      return `${token ?? ''}|${companyId}`;
    },
    [token]
  );

  const fetchCompanyInfo = useCallback(
    async (force = false) => {
      if (!company?.id || !token) {
        return;
      } else {
        // When API returns {message: "User doesn't have notifications"}
        console.log('📭 No notifications available');
        setNotifications([]);
      }

      const cacheKey = buildCompanyInfoKey(company.id);

      if (!force) {
        if (companyInfoCache.has(cacheKey)) {
          const cached = companyInfoCache.get(cacheKey);
          setCompanyInfo(cached);
          return cached;
        }

        const inFlight = companyInfoPromiseMap.get(cacheKey);
        if (inFlight) {
          return inFlight;
        }
      } else {
        companyInfoCache.delete(cacheKey);
        companyInfoPromiseMap.delete(cacheKey);
      }

      const request = ManagementService.getCompanyInfo(api, company.id.toString(), { headers })
        .then((response) => {
          const info = (response as any)?.data ?? response;
          if (!info) {
            console.error('Error Fetch CompanyInfo');
            return undefined;
          }
          companyInfoCache.set(cacheKey, info);
          setCompanyInfo(info);
          return info;
        })
        .catch((error) => {
          console.error('Error fetching company info:', error);
          return companyInfoCache.get(cacheKey);
        })
        .finally(() => {
          companyInfoPromiseMap.delete(cacheKey);
        });

      companyInfoPromiseMap.set(cacheKey, request);
      return request;
    },
    [api, buildCompanyInfoKey, company?.id, headers, token]
  );

  useEffect(() => {
    if (!company?.id) {
      setCompanyInfo(undefined);
      return;
    }

    const cacheKey = buildCompanyInfoKey(company.id);
    if (companyInfoCache.has(cacheKey)) {
      setCompanyInfo(companyInfoCache.get(cacheKey));
      return;
    }

    const inFlight = companyInfoPromiseMap.get(cacheKey);
    if (inFlight) {
      void inFlight.then((info) => {
        if (info) {
          companyInfoCache.set(cacheKey, info);
          setCompanyInfo(info);
        }
      });
    }
  }, [buildCompanyInfoKey, company?.id]);

  const fetchDocuments = useCallback(
    async (force = false) => {
      if (!company?.id || !token) {
        setDocuments([]);
        return [] as IDocument[];
      }

      const cacheKey = `${token}|${company.id}`;

      if (force) {
        documentsCache.delete(cacheKey);
        documentsPromiseMap.delete(cacheKey);
      } else {
        if (documentsCache.has(cacheKey) && documentsCache.get(cacheKey)) {
          const cached = documentsCache.get(cacheKey) ?? [];
          setDocuments(cached);
          return cached;
        }

        if (documentsPromiseMap.has(cacheKey)) {
          return documentsPromiseMap.get(cacheKey);
        }
      }

      setDocuments(undefined);

      const request = AccountService.getCompanyDocuments(api, company.id.toString(), { headers })
        .then((response) => {
          const responseData = (response as GetCompanyDocumentsResponse).data;
          const mappedDocuments = Array.isArray(responseData?.shareholders)
            ? getDocsFromShareholders(responseData)
            : [];

          if (mappedDocuments.length > 3) {
            mappedDocuments.length = 3;
          }

          documentsCache.set(cacheKey, mappedDocuments);
          setDocuments(mappedDocuments);
          return mappedDocuments;
        })
        .catch((error) => {
          console.error('Error fetching documents:', error);
          documentsCache.set(cacheKey, []);
          setDocuments([]);
          return [];
        })
        .finally(() => {
          documentsPromiseMap.delete(cacheKey);
        });

      documentsPromiseMap.set(cacheKey, request);
      return request;
    },
    [api, company?.id, headers, token]
  );

  const fetchNotifications = useCallback(
    async (force = false) => {
      if (!company?.id || !token) {
        return [] as INotification[];
      }

      const cacheKey = `${token}|${company.id}`;

      if (force) {
        notificationsCache.delete(cacheKey);
        notificationsPromiseMap.delete(cacheKey);
      } else {
        if (notificationsCache.has(cacheKey) && notificationsCache.get(cacheKey)) {
          return notificationsCache.get(cacheKey);
        }

        if (notificationsPromiseMap.has(cacheKey)) {
          return notificationsPromiseMap.get(cacheKey);
        }
      }

      setNotificationsRefreshing(true);

      const request = AuthService.getNotificationsMe(api, { headers })
        .then((response: any) => {
          if (!response?.message && Array.isArray(response)) {
            const limited = [...response];
            if (limited.length > 3) {
              limited.length = 3;
            }
            notificationsCache.set(cacheKey, limited);
            setNotifications(limited);
            return limited as INotification[];
          }

          notificationsCache.set(cacheKey, []);
          setNotifications([]);
          return [] as INotification[];
        })
        .catch((error) => {
          console.error('Error fetching notifications:', error);
          notificationsCache.set(cacheKey, []);
          setNotifications([]);
          return [] as INotification[];
        })
        .finally(() => {
          notificationsPromiseMap.delete(cacheKey);
          setNotificationsRefreshing(false);
        });

      notificationsPromiseMap.set(cacheKey, request);
      return request;
    },
    [api, company?.id, headers, token]
  );

  const latestAnnualFeeRef = useRef<any>(null);
  const latestAnnualFeePromiseRef = useRef<Promise<any> | null>(null);
  const latestAnnualFeeAttemptedRef = useRef(false);

  const fetchLatestAnnualFee = useCallback(async (force = false) => {
    if (!company?.id || !token) {
      return null;
    }

    if (force) {
      latestAnnualFeeRef.current = null;
      latestAnnualFeePromiseRef.current = null;
      latestAnnualFeeAttemptedRef.current = false;
    } else {
      if (latestAnnualFeeAttemptedRef.current) {
        return latestAnnualFeeRef.current;
      }

      if (latestAnnualFeePromiseRef.current) {
        return latestAnnualFeePromiseRef.current;
      }
    }

    const request = BillingService.getLatestAnnualFee(api, { headers })
      .then((data) => {
        latestAnnualFeeAttemptedRef.current = true;
        latestAnnualFeeRef.current = data;
        startTransition(() => {
          setLatestAnnualFee(data);
        });
        return data;
      })
      .catch((error) => {
        console.error('Error fetching latest annual fee:', error);
        latestAnnualFeeAttemptedRef.current = true;
        latestAnnualFeeRef.current = null;
        startTransition(() => {
          setLatestAnnualFee(null);
        });
        return null;
      })
      .finally(() => {
        latestAnnualFeePromiseRef.current = null;
      });

    latestAnnualFeePromiseRef.current = request;

    return request;
  }, [api, company?.id, headers, token]);

  useEffect(() => {
    latestAnnualFeeRef.current = latestAnnualFee;
  }, [latestAnnualFee]);

  const fetchUserContract = useCallback(
    async (force = false) => {
      if (!company?.id || !token) {
        userContractCache.set(buildCompanyInfoKey(company?.id), null);
        setIsSigned(false);
        setShowMessage(true);
        setUrlContact('');
        return null;
      }

      const cacheKey = buildCompanyInfoKey(company.id);

      if (force) {
        userContractCache.set(cacheKey, null);
        userContractPromiseMap.delete(cacheKey);
      } else {
        const cached = userContractCache.get(cacheKey);
        if (cached) {
          setIsSigned(cached.signed);
          setShowMessage(!cached.signed);
          setUrlContact(cached.url);
          return cached;
        }

        if (userContractPromiseMap.has(cacheKey)) {
          return userContractPromiseMap.get(cacheKey);
        }
      }

      const request = AccountService.getUserContract(api, 'type=contract', { headers })
        .then(({ data }: any) => {
          if (Array.isArray(data) && data.length > 0) {
            const { signed, url } = data[0];
            const normalized = { signed: Boolean(signed), url: url ?? '' };
            userContractCache.set(cacheKey, normalized);
            setIsSigned(normalized.signed);
            setShowMessage(!normalized.signed);
            setUrlContact(normalized.url);
            return normalized;
          }

          const normalized = { signed: false, url: '' };
          userContractCache.set(cacheKey, normalized);
          setIsSigned(false);
          setShowMessage(true);
          setUrlContact('');
          return normalized;
        })
        .catch((error) => {
          console.error('Error fetching user contract:', error);
          const cached = userContractCache.get(cacheKey);
          if (!force && cached) {
            setIsSigned(cached.signed);
            setShowMessage(!cached.signed);
            setUrlContact(cached.url);
            return cached;
          }
          const normalized = { signed: false, url: '' };
          userContractCache.set(cacheKey, normalized);
          setIsSigned(false);
          setShowMessage(true);
          setUrlContact('');
          return normalized;
        })
        .finally(() => {
          userContractPromiseMap.delete(cacheKey);
        });

      userContractPromiseMap.set(cacheKey, request);
      return request;
    },
    [api, company?.id, headers, token]
  );

  const markNotificationAsRead = useCallback(async (notificationId: number | string) => {
    if (!notificationId || !token) {
      return;
    }

    try {
      await AuthService.markNotificationsAsRead(api, { notification_id: notificationId }, { headers });
      setNotifications((prev) =>
        Array.isArray(prev)
          ? prev.map((notification: any) =>
              notification.id === notificationId
                ? { ...notification, read_at: notification.read_at ?? new Date().toISOString() }
                : notification
            )
          : prev
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [api, headers, token]);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!company?.slug || !token) {
      return;
    }

    try {
      await AuthService.markNotificationsAsRead(
        api,
        { company_slug: company.slug },
        { headers, params: { mark_all: true } }
      );

      setNotifications((prev) =>
        Array.isArray(prev)
          ? prev.map((notification) => ({ ...notification, read_at: notification.read_at ?? new Date().toISOString() }))
          : prev
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [api, headers, company?.slug, token]);

  useEffect(() => {
    if (!token) {
      setCompanies([]);
      companiesCache.clear();
      companiesPromiseMap.clear();
      companyInfoCache.clear();
      companyInfoPromiseMap.clear();
      return;
    }

    const cacheKey = buildCompaniesCacheKey();
    const cachedCompanies = companiesCache.get(cacheKey);
    if (cachedCompanies) {
      setCompanies(cachedCompanies);
    }

    void fetchCompanies();
  }, [buildCompaniesCacheKey, fetchCompanies, token]);

  useEffect(() => {
    if (!token || !company?.id) {
      setDocuments([]);
      setFetchingData(false);
      return;
    }

    setFetchingData(true);

    Promise.allSettled([
      fetchCompanyInfo(),
      fetchDocuments(),
      fetchNotifications(),
      fetchUserContract(),
      fetchLatestAnnualFee()
    ]).finally(() => {
      setFetchingData(false);
    });
  }, [token, company?.id, fetchCompanyInfo, fetchDocuments, fetchNotifications, fetchUserContract, fetchLatestAnnualFee]);

  const unreadNotifications = useMemo(
    () => (Array.isArray(notifications) ? notifications.filter((notification) => !notification.read_at).length : 0),
    [notifications]
  );

  const valuesContext = useMemo(
    () => ({
      companyInfo,
      documents,
      notifications,
      companies,
      latestAnnualFee,
      unreadNotifications,
      refreshCompanyInfo: fetchCompanyInfo,
      refreshDocuments: fetchDocuments,
      refreshNotifications: fetchNotifications,
      refreshCompanies: fetchCompanies,
      refreshLatestAnnualFee: fetchLatestAnnualFee,
      refreshUserContract: fetchUserContract,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      isSigned,
      showMessage,
      urlContract,
      company,
      token,
      fetchingData,
      fetchingCompanies,
      notificationsRefreshing,
      setNotificationsRefreshing
    }),
    [
      companyInfo,
      documents,
      notifications,
      companies,
      latestAnnualFee,
      unreadNotifications,
      fetchCompanyInfo,
      fetchDocuments,
      fetchNotifications,
      fetchCompanies,
      fetchLatestAnnualFee,
      fetchUserContract,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      isSigned,
      showMessage,
      urlContract,
      company,
      token,
      fetchingData,
      fetchingCompanies,
      notificationsRefreshing,
      setNotificationsRefreshing
    ]
  );

  return <DashboardCustomerContext.Provider value={valuesContext}>{children}</DashboardCustomerContext.Provider>;
};

export { DashboardCustomerContext, DashboardCustomerProvider };
